import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Loader2, GripVertical, LayoutGrid } from 'lucide-react';
import api from '../../api/client';
import { resolveImage } from './ImageUpload';

/**
 * Reusable curated-list editor. Backs a single settings array (e.g.
 * `homepage_deals` or `homepage_trending`) with a searchable dropdown +
 * ordered chip list + drag-to-reorder.
 *
 * Kept independent from LinkedRecommendationsEditor because the persistence
 * shape is different (site-settings vs per-product) and the UX benefits from
 * a bigger row (image + name + price + brand).
 */
const CuratedListEditor = ({ title, subtitle, testidPrefix, maxItems, ids, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchTimer = useRef(null);
  const dragFromRef = useRef(null);

  const hydrate = useCallback(async () => {
    if (!ids || ids.length === 0) { setDocs([]); return; }
    setLoading(true);
    try {
      // /products doesn't fetch-by-ids so use each id's /products/{id} in parallel,
      // capped at maxItems to keep the ceiling tight.
      const capped = ids.slice(0, maxItems || 20);
      const results = await Promise.all(
        capped.map(id => api.get(`/products/${id}`).then(r => r.data).catch(() => null))
      );
      setDocs(results.filter(Boolean));
    } catch (e) { /* keep stale */ }
    finally { setLoading(false); }
  }, [ids.join(','), maxItems]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { hydrate(); }, [hydrate]);

  const runSearch = (q) => {
    clearTimeout(searchTimer.current);
    setQuery(q);
    if (!q.trim() || q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await api.get('/products/suggest', { params: { q, limit: 10 } });
        const excluded = new Set(ids);
        setResults((r.data || []).filter(p => !excluded.has(p.id)));
      } catch (e) { setResults([]); }
      finally { setSearching(false); }
    }, 220);
  };

  const addItem = async (target) => {
    if (maxItems && ids.length >= maxItems) return;
    await onChange([...ids, target.id]);
    setQuery(''); setResults([]);
  };
  const removeItem = async (targetId) => {
    await onChange(ids.filter(x => x !== targetId));
  };

  const handleDragStart = (idx) => (e) => { dragFromRef.current = idx; e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (idx) => async (e) => {
    e.preventDefault();
    const from = dragFromRef.current;
    dragFromRef.current = null;
    if (from == null || from === idx) return;
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    await onChange(next);
  };

  const moveByStep = async (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= ids.length) return;
    const next = [...ids];
    [next[idx], next[target]] = [next[target], next[idx]];
    await onChange(next);
  };

  const atCap = maxItems && ids.length >= maxItems;

  return (
    <section className="bg-white border border-neutral-200 rounded-sm p-5" data-testid={`${testidPrefix}-section`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="bg-orange-50 text-[#F46A25] p-2 rounded-sm">
          <LayoutGrid className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-[#12263A]">{title}</h3>
          <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="text-[11px] font-semibold text-neutral-500 whitespace-nowrap">
          {ids.length}{maxItems ? ` / ${maxItems}` : ''} selected
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          type="search"
          value={query}
          onChange={e => runSearch(e.target.value)}
          disabled={atCap}
          placeholder={atCap ? `Maximum ${maxItems} products selected` : 'Search products by name, brand or SKU…'}
          data-testid={`${testidPrefix}-search`}
          className="w-full text-sm pl-9 pr-3 py-2 border border-neutral-300 rounded-sm focus:outline-none focus:border-[#F46A25] disabled:bg-neutral-50 disabled:text-neutral-400"
        />
        {searching && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-[#F46A25]" />}
      </div>

      {results.length > 0 && (
        <div className="mt-1 bg-white border border-neutral-200 rounded-sm shadow-md max-h-64 overflow-y-auto z-10 relative" data-testid={`${testidPrefix}-results`}>
          {results.map(r => (
            <button
              key={r.id}
              type="button"
              onClick={() => addItem(r)}
              data-testid={`${testidPrefix}-add-${r.id}`}
              className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-orange-50 border-b border-neutral-100 last:border-b-0"
            >
              <img src={resolveImage(r.image)} alt="" className="w-8 h-8 object-cover flex-shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-[#12263A] truncate">{r.name}</span>
                <span className="text-[11px] text-neutral-500">{r.brand} · SKU {r.sku}</span>
              </span>
              <span className="text-[#F46A25] font-bold whitespace-nowrap">£{Number(r.price).toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="mt-4 space-y-1.5" data-testid={`${testidPrefix}-list`}>
        {loading && <div className="text-xs text-neutral-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>}
        {!loading && docs.length === 0 && (
          <div className="text-xs text-neutral-500 italic py-4 text-center border border-dashed border-neutral-200 rounded-sm">
            No products selected. Section is hidden on the storefront.
          </div>
        )}
        {docs.map((r, idx) => (
          <div
            key={r.id}
            draggable
            onDragStart={handleDragStart(idx)}
            onDragOver={handleDragOver}
            onDrop={handleDrop(idx)}
            data-testid={`${testidPrefix}-item-${r.id}`}
            className="flex items-center gap-3 px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-sm text-sm group"
          >
            <GripVertical className="w-4 h-4 text-neutral-400 cursor-grab flex-shrink-0" />
            <div className="w-6 text-[11px] font-bold text-neutral-500 tabular-nums">#{idx + 1}</div>
            <img src={resolveImage(r.image)} alt="" className="w-9 h-9 object-cover flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#12263A] truncate">{r.name}</div>
              <div className="text-[11px] text-neutral-500">{r.brand} · £{Number(r.price).toFixed(2)}</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => moveByStep(idx, -1)}
                disabled={idx === 0}
                title="Move up"
                data-testid={`${testidPrefix}-up-${r.id}`}
                className="text-neutral-500 hover:text-[#F46A25] text-xs px-1.5 py-0.5 border border-neutral-300 hover:border-[#F46A25] rounded-sm disabled:opacity-30"
              >↑</button>
              <button
                type="button"
                onClick={() => moveByStep(idx, +1)}
                disabled={idx === docs.length - 1}
                title="Move down"
                data-testid={`${testidPrefix}-down-${r.id}`}
                className="text-neutral-500 hover:text-[#F46A25] text-xs px-1.5 py-0.5 border border-neutral-300 hover:border-[#F46A25] rounded-sm disabled:opacity-30"
              >↓</button>
              <button
                type="button"
                onClick={() => removeItem(r.id)}
                data-testid={`${testidPrefix}-remove-${r.id}`}
                className="text-neutral-500 hover:text-red-600 text-xs p-1 border border-neutral-300 hover:border-red-500 rounded-sm ml-1"
                aria-label="Remove"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/**
 * Homepage Curation tab. Two curated lists — "Deals of the week" (up to 8) and
 * "Trending products" (up to 8) — persist into site settings and flow to the
 * homepage via `/api/products/homepage/deals` and `/api/products/homepage/trending`.
 */
export const HomepageCurationAdmin = ({ settings, onSave }) => {
  const dealsIds = settings?.homepage_deals || [];
  const trendingIds = settings?.homepage_trending || [];

  return (
    <div className="space-y-6" data-testid="homepage-curation-admin">
      <div className="bg-orange-50 border border-orange-200 rounded-sm p-4 text-sm text-[#12263A]">
        <b>Homepage Curation</b> — Hand-pick the products shown in the "Deals of the week" and "Trending products" rows on the homepage.
        {' '}Leave a list empty to hide its row from the storefront.
      </div>

      <CuratedListEditor
        title="Deals of the week"
        subtitle="Featured on the orange band near the top of the homepage. Up to 8 products, ordered left-to-right."
        testidPrefix="homepage-deals"
        maxItems={8}
        ids={dealsIds}
        onChange={(next) => onSave({ homepage_deals: next })}
      />

      <CuratedListEditor
        title="Trending products"
        subtitle="Shown below the deals row. Up to 8 products in your chosen order."
        testidPrefix="homepage-trending"
        maxItems={8}
        ids={trendingIds}
        onChange={(next) => onSave({ homepage_trending: next })}
      />
    </div>
  );
};

export default HomepageCurationAdmin;
